import axios, { AxiosResponse, Method } from 'axios'

import { LatencyMetric } from '@amzn/ihm-associate-tools-js-lib'

interface IRawAjaxRequestHandlerInput {
    sensitive?: boolean | false,
    data: {
        url: string,
        method: Method,
        headers: {[key: string]: string},
        body: any,
    }
    ,
}

/*
 * same data structure that IhmSeaward returns https://code.amazon.com/packages/IhmSeaward/blobs/8596f1d97e7819ef3f47fc5413f233748fb58e49/--/source/js/crossFrame/rawAjaxRequestListener.js#L63-L68
 */
export interface IRawAjaxRequestHandlerResponse {
    responseURL: string,
    status: number,
    statusText: string,
    data: unknown
}

/*
 * Some request from tools already have authentication headers set. This handler allows to make requests without modifying headers
 */
export class RawAjaxRequestHandler {

    /**
     * Returns a Promise which resolves to an http response (regardless of the HTTP response code).
     *
     * @param {object} input - Options for the request.
     *  {
     *      data: {
     *          url: {String}, // required,
     *          method: {String}, // required. See {@link https://developer.mozilla.org/en-US/docs/Web/API/Request/method}
     *          headers: {Object}, // optional headers.,
     *          body: {any}, // optional body to include with the request.
     *      }
     *      sensitive: {Boolean} // optional defaults to FALSE
     *  }
     *  The options object is modeled after the {@link  https://developer.mozilla.org/en-US/docs/Web/API/Request} object.
     * @return {Promise} - Resolves with essential HTTP Response properties.
     *  Essential HTTP Response Properties:
     *      {
     *          status: {Number} - The HTTP status code.
     *          statusText: {String} - The status text (i.e. "OK")
     *          responseURL: {String} - https://amazon.com
     *          data: {unknown} - The data in the response
     *      }
     */
    public static async listener (input: IRawAjaxRequestHandlerInput): Promise<IRawAjaxRequestHandlerResponse> {
        console.debug('Making request ',
        RawAjaxRequestHandler.getDataForLoggingEvent(input.sensitive, input.data))

        console.log('ATBase: Making call to', input.data.url)
        const ajaxRequestLatencyMetric = new LatencyMetric(undefined, 'RawAjaxRequestLatencyMetric', 'IhmFlyingDutchman', 'RawAjaxRequestHandler')
        ajaxRequestLatencyMetric.start()
        return axios({
            url: input.data.url,
            method: input.data.method,
            data: input.data.body,
            headers: input.data.headers,
            withCredentials: false, // important to set false because that allows cross origin requests to through (with no cookies)
        }).then((axiosResponse: AxiosResponse) => {
            ajaxRequestLatencyMetric.stop()
            const response = {
                responseURL: axiosResponse.config.url,
                status: axiosResponse.status,
                statusText: axiosResponse.statusText,
                data: axiosResponse.data,
            }
            console.debug('Successful response',
            RawAjaxRequestHandler.getDataForLoggingEvent(input.sensitive, response))
            return response
        }).catch((catchResponse) => {
            ajaxRequestLatencyMetric.stop()
            console.warn('Error response',
                RawAjaxRequestHandler.getDataForLoggingEvent(input.sensitive, catchResponse))
            return Promise.reject(catchResponse)
        })
    }

    private static getDataForLoggingEvent (sensitive: boolean, data: any): string {
        if (sensitive) {
            return '<-- Redacted since sensitive=true -->'
        }
        return JSON.stringify(data)
    }
}
